import { LegalDocument } from '@westhetics/legal/react'
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ListRows,
  Skeleton,
  StatusTag,
  Surface,
  formatDate,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { LoadError } from '@/app/load-error'
import { useApi } from '@/lib/api-context'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import {
  useArchiveTemplate,
  useTemplateVersion,
  useTemplateVersions,
  useUpdateTemplate,
  type Template,
  type Version,
  type VersionSummary,
} from './api'
import { isRequiredCategory } from './clinic-activation'

/**
 * Aba Versões: a linha do tempo do documento. Cada versão é imutável e diz quem publicou, quem
 * revisou, quando e por quê (RN-DOC-11, 13 e 14). Daqui se lê uma versão, se compara com a anterior
 * e se traz o texto de volta como rascunho.
 */
export function TemplateVersionsTab({ template }: { template: Template }) {
  const navigate = useNavigate()
  const api = useApi()
  const versions = useTemplateVersions(template.id)
  const update = useUpdateTemplate(template.id)
  const archive = useArchiveTemplate(template.id)
  const [reading, setReading] = useState<number | null>(null)
  const [restoring, setRestoring] = useState<VersionSummary | null>(null)
  const [restoreBusy, setRestoreBusy] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const isArchived = template.state === 'archived'

  async function restoreAsDraft(version: VersionSummary) {
    setRestoreBusy(true)
    try {
      const full = await api.get<Version>(
        `/documents/templates/${template.id}/versions/${version.number}`,
      )
      await update.mutateAsync({
        draft: { markdown: full.markdown, revision: template.draftRevision },
      })
      toast(`O rascunho agora é o texto da versão ${version.label} — nada foi publicado`)
      navigate(routes.admin.template(template.id, 'editor'))
    } catch (error) {
      toast(errorMessage(error))
    } finally {
      setRestoreBusy(false)
      setRestoring(null)
    }
  }

  const list = versions.data ?? []

  return (
    <div className="flex max-w-[1120px] flex-col gap-4">
      {versions.isError ? (
        <LoadError
          title="Não conseguimos carregar as versões"
          error={versions.error}
          onRetry={() => void versions.refetch()}
        />
      ) : versions.isPending ? (
        <div role="status" aria-label="Carregando" className="flex flex-col gap-2">
          <Skeleton className="h-[72px] w-full" />
          <Skeleton className="h-[72px] w-full" />
          <Skeleton className="h-[72px] w-full" />
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          title="Nenhuma versão publicada ainda"
          description="A versão é o texto exato que as clínicas usam e que o paciente assina. Ela nasce quando o rascunho é publicado, e não muda mais."
          action={
            <Button asChild>
              <Link to={routes.admin.template(template.id, 'editor')}>Abrir o editor</Link>
            </Button>
          }
        />
      ) : (
        <ListRows>
          {list.map((version, index) => {
            const previous = list[index + 1]
            const isReading = reading === version.number
            return (
              <div key={version.id} className="border-hairline border-b last:border-b-0">
                <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-4 py-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display text-name text-ink font-normal">
                        v{version.number} · {version.label}
                      </h3>
                      {index === 0 && !isArchived ? (
                        <StatusTag level="done">Vigente</StatusTag>
                      ) : null}
                      {version.requiresIcpCertificate ? (
                        <StatusTag level="waiting">Exige certificado ICP-Brasil</StatusTag>
                      ) : null}
                    </div>
                    {version.updateNote ? (
                      <p className="font-body text-body text-ink mt-2 font-light">
                        {version.updateNote}
                      </p>
                    ) : null}
                    <p className="font-body text-secondary mt-2 text-[12px] leading-[1.6] font-light">
                      Publicada em {formatDate(new Date(version.publishedAt), { year: true })} por{' '}
                      {titleCase(version.publishedBy.name)} · revisada por {version.reviewedBy} em{' '}
                      {formatDate(new Date(`${version.reviewedAt}T12:00:00`), { year: true })} ·
                      hash {version.shortHash}
                    </p>
                  </div>
                  <div className="font-body text-eyebrow text-label flex shrink-0 flex-wrap items-center gap-4 font-light tracking-normal">
                    <button
                      type="button"
                      aria-expanded={isReading}
                      onClick={() => setReading(isReading ? null : version.number)}
                      className="hover:text-ink min-h-touch-desk max-desk:min-h-touch cursor-pointer"
                    >
                      {isReading ? 'fechar' : 'ler'}
                    </button>
                    {previous ? (
                      <Link
                        to={`${routes.admin.template(template.id, 'compare')}?from=${previous.number}&to=${version.number}`}
                        className="hover:text-ink min-h-touch-desk max-desk:min-h-touch flex items-center"
                      >
                        comparar com a anterior
                      </Link>
                    ) : null}
                    {!isArchived ? (
                      <button
                        type="button"
                        onClick={() => setRestoring(version)}
                        className="hover:text-ink min-h-touch-desk max-desk:min-h-touch cursor-pointer"
                      >
                        usar como rascunho
                      </button>
                    ) : null}
                  </div>
                </div>
                {isReading ? (
                  <VersionReader templateId={template.id} number={version.number} />
                ) : null}
              </div>
            )
          })}
        </ListRows>
      )}

      <Surface className="flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-[560px]">
          <div className="font-display text-lead text-ink leading-[1.3] font-normal">
            {isArchived ? 'Restaurar documento' : 'Arquivar documento'}
          </div>
          <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
            {isArchived
              ? 'Volta ao catálogo e às escolhas das clínicas, com as versões e os vínculos que tinha.'
              : 'Sai do catálogo e das escolhas novas em todas as clínicas. As versões e os documentos já emitidos continuam legíveis.'}
          </p>
        </div>
        {isArchived ? (
          <Button
            variant="outline"
            loading={archive.isPending}
            loadingLabel="Restaurando…"
            onClick={() =>
              archive.mutate('restore', {
                onSuccess: () => toast(`"${template.name}" restaurado — volta às clínicas`),
                onError: (error) => toast(errorMessage(error)),
              })
            }
          >
            Restaurar
          </Button>
        ) : (
          <Button variant="outline" onClick={() => setArchiving(true)}>
            Arquivar
          </Button>
        )}
      </Surface>

      <ConfirmDialog
        open={restoring !== null}
        onOpenChange={(open) => {
          if (!open) setRestoring(null)
        }}
        tone="primary"
        title={`Usar a versão ${restoring?.label ?? ''} como rascunho?`}
        effect={
          template.hasUnpublishedChanges
            ? 'O rascunho atual é substituído pelo texto dessa versão: as alterações que ainda não foram publicadas se perdem. Nada chega às clínicas até você publicar.'
            : 'O rascunho passa a ser o texto dessa versão. Nada chega às clínicas até você publicar.'
        }
        confirmLabel="Usar como rascunho"
        loading={restoreBusy}
        loadingLabel="Trazendo o texto…"
        onConfirm={() => restoring && void restoreAsDraft(restoring)}
      />

      <ConfirmDialog
        open={archiving}
        onOpenChange={setArchiving}
        title={`Arquivar "${template.name}"?`}
        effect={`O documento sai do catálogo e não pode mais ser escolhido em nenhuma clínica${
          template.usage.clinicCount > 0
            ? ` (hoje ${template.usage.clinicCount === 1 ? '1 clínica o usa' : `${template.usage.clinicCount} clínicas o usam`})`
            : ''
        }.${
          // Onde a clínica não pode ficar sem, o padrão do catálogo assume (RN-DOC-49).
          isRequiredCategory(template.category) && template.usage.clinicCount > 0
            ? ' As clínicas que o usam passam para o padrão do catálogo.'
            : ''
        } Os documentos já emitidos e as versões continuam legíveis — nada assinado se desfaz. Volta a qualquer momento.`}
        confirmLabel="Arquivar"
        loading={archive.isPending}
        loadingLabel="Arquivando…"
        onConfirm={() =>
          archive.mutate('archive', {
            onSuccess: () => {
              setArchiving(false)
              toast(`"${template.name}" arquivado — volta a qualquer momento`)
            },
            onError: (error) => {
              setArchiving(false)
              toast(errorMessage(error))
            },
          })
        }
      />
    </div>
  )
}

/** A versão como foi publicada, renderizada pelo `@westhetics/legal`. */
function VersionReader({ templateId, number }: { templateId: string; number: number }) {
  const version = useTemplateVersion(templateId, number)
  if (version.isError) {
    return (
      <div className="px-4 pb-4">
        <LoadError
          title="Não conseguimos carregar o texto desta versão"
          error={version.error}
          onRetry={() => void version.refetch()}
        />
      </div>
    )
  }
  if (!version.data) {
    return (
      <div role="status" aria-label="Carregando" className="px-4 pb-4">
        <Skeleton className="h-[240px] w-full" />
      </div>
    )
  }
  return (
    <div className="border-hairline bg-pampas border-t px-4 py-6">
      <div className="bg-surface border-hairline mx-auto max-w-[760px] border px-6 py-6">
        <LegalDocument markdown={version.data.markdown} />
      </div>
    </div>
  )
}
